import { markdownSchema, FormInitializerReturnType, stringSchema, ValidationMessageKeys } from 'src/Configuration/validation'
import { NotificationForm, NotificationOverviewModel, NotificationType, PlatformType } from '../models/notifications.model'
import { NotificationUserLevel } from '../types'
import { boolean, mixed, object, string, array } from 'yup'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import moment, { Moment } from 'moment'

const MESSAGE_TITLE_MAX_SYMBOLS = 255
const MESSAGE_TITLE_MIN_SYMBOLS = 3

const MESSAGE_TEXT_MAX_SYMBOLS = 65535
const MESSAGE_TEXT_MIN_SYMBOLS = 3

/**
 *  Initializes the form for creating or editing a notification
 * @param notification NotificationOverviewModel
 * @returns initialValues and validationSchema for notification form
 */
export const useNotificationFormInitializer = (
  notification?: NotificationOverviewModel
): FormInitializerReturnType<NotificationForm, NotificationForm> => {
  const { t } = useCustomTranslation()
  const stringSchemaWithTranslation = stringSchema(t)
  const markdownSchemaWithTranslation = markdownSchema(t)

  const initialValues = {
    title: notification?.title ?? '',
    text: notification?.text ?? '',
    startAt: moment(notification?.startAt) ?? moment(),
    stopAt: notification?.stopAt ? moment(notification.stopAt) : null,
    platform: notification?.platform ?? PlatformType.Both,
    type: notification?.type ?? NotificationType.Optional,
    userLevel: defineSelectedUserLevel(notification) ?? 'ALL',
    ext_service: notification?.ext_service ?? true /* always implicitly included */,
    e3dc_service: notification?.e3dc_service ?? true /* always implicitly included */,
    e3dc_root: notification?.e3dc_root ?? true /* always implicitly included */,
    e3dc_admin: notification?.e3dc_admin ?? true /* always implicitly included */,
    user: notification?.user ?? true,
    installer: notification?.installer ?? true,
    partner: notification?.partner ?? true,
    active: notification?.active ?? true,
    fileKeys: [],
  }

  const validationSchema = object().shape({
    title: stringSchemaWithTranslation({ maxLength: MESSAGE_TITLE_MAX_SYMBOLS, minLength: MESSAGE_TITLE_MIN_SYMBOLS }).required(),
    text: markdownSchemaWithTranslation({ maxLength: MESSAGE_TEXT_MAX_SYMBOLS, minLength: MESSAGE_TEXT_MIN_SYMBOLS }).required(),
    startAt: mixed<Moment>()
      .required()
      .test('is-moment', t(ValidationMessageKeys.format), value => moment(value).isValid()),
    stopAt: mixed<Moment>()
      .nullable()
      .test('is-moment', t(ValidationMessageKeys.format), value => {
        if (value === null) return true
        return moment(value).isValid()
      })
      .when('startAt', ([minDate], schema) => {
        const minDateAsMoment = moment(minDate)

        return minDateAsMoment
          ? schema.test('isAfterStartAt', t('validation.error-message.min-date'), (value?: Moment | null) => {
              return value?.isAfter(minDateAsMoment) || value === null
            })
          : schema
      }),
    platform: string<PlatformType>().required(),
    type: string<NotificationType>().required(),
    userLevel: string<NotificationUserLevel>().required(),
    active: boolean().required(),
    fileKeys: array(string().required()).optional(),
  })
  return { initialValues, validationSchema }
}

export const defineSelectedUserLevel = (notification?: NotificationOverviewModel): NotificationUserLevel | undefined => {
  if (notification?.user && notification.installer && notification.partner) {
    /* All user levels was selected. */
    return 'ALL'
  }
  if (notification?.installer && notification.partner && !notification.user) {
    /* Installer and Partner was selected. */
    return 'PARTNER_AND_INSTALLER'
  }
  if (notification && !notification.installer && !notification.partner && !notification.user) {
    /* Only service users. */
    return 'INTERNAL'
  }
  if (notification?.user) {
    return 'USER'
  }
  return undefined
}
